import type * as ents from '@attain-apps/entities'

import type * as admin from '../../admin'
import type * as me from '../../me'

import type { Feature } from './feature'
import * as models from './models'

/**
 * Selectors for the user-assignment workflow state.
 */
export class Selectors {
  /**
   * Represents the list of users available for assignment for the currently
   * selected entity, which can be a tenant, zone, or application (identified by
   * its ID).
   * This list is dynamically filtered based on the selected context to show
   * only relevant users.
   */
  public readonly users
  /**
   * List of currently selected user ids ready for assignment
   */
  public readonly selectedUserIds
  /**
   * List of selected users ready for assignment
   */
  public readonly selectedUsers
  /**
   * Returns true if assignments are being edited
   */
  public readonly isEditing
  /**
   * Current access level in editing mode
   */
  public readonly access
  /**
   * Access flags available for the current assignment context and selected users.
   */
  public readonly availableAccessFlags
  /**
   * List of assignments ready for api request
   */
  public readonly assignments
  /**
   * User that is marked for unassignment
   */
  public readonly markedUserForUnassignment
  /**
   * Message for the latest failed assignment mutation, if any.
   */
  public readonly mutationErrorMessage
  /**
   * @see {@link ents.core.admin.Access.Assignment} that is in the process of
   * deletion
   */
  public readonly deletingAssignment
  /**
   * List of currently selected page IDs that will be excluded from user's access
   * permissions
   */
  public readonly excludePages
  /**
   * List of currently selected page IDs that will be included in user's access
   * permissions
   */
  public readonly includePages

  constructor(
    feature: Feature,
    contextsFeature: admin.contexts.Feature,
    meFeature: me.Feature,
    appsEc: admin.apps.Collection,
  ) {
    this.users = feature.state.project(state => state.users)
    this.selectedUserIds = feature.state.project(state => state.selectedUserIds)
    this.selectedUsers = this.users.project(
      this.selectedUserIds.asSelector,
      (users, selectedUserIds) =>
        users.filter(user => selectedUserIds.includes(user.id)),
    )
    this.isEditing = feature.state.project(state => state.isEditing)
    this.availableAccessFlags = this.selectedUsers.project(
      contextsFeature.selectors.activeEntityContext.asSelector,
      meFeature.selectors.currentUser.asSelector,
      appsEc.plugins.currentEntityPlugin.feature.selectors.getEntity()
        .asSelector,
      (selectedUsers, activeContext, currentUser, app) =>
        models.User.getAccessFlags({
          activeContext,
          app,
          currentUser,
          selectedUsers,
        }),
    )
    this.access = feature.state
      .project(state => state.access)
      .project(
        this.availableAccessFlags.asSelector,
        models.User.limitAccessToFlags,
      )
    this.excludePages = feature.state.project(state => state.excludePages)
    this.includePages = feature.state.project(state => state.includePages)
    this.assignments = this.selectedUserIds.project(
      this.access.asSelector,
      contextsFeature.selectors.activeEntityContext.asSelector,
      this.excludePages.asSelector,
      this.includePages.asSelector,
      (userIds, access, context, excludePages, includePages) =>
        userIds.map(userId => ({
          ...context,
          access,
          excludePages,
          includePages,
          userId,
        })),
    )
    this.markedUserForUnassignment = feature.state.project(
      state => state.markedUserForUnassignment,
    )
    this.mutationErrorMessage = feature.state.project(
      state => state.mutationError?.message,
    )
    this.deletingAssignment = this.markedUserForUnassignment.project(
      contextsFeature.selectors.activeEntityContext.asSelector,
      (user, context) =>
        context && user?.id
          ? {
              ...context,
              access: 0,
              userId: user.id,
            }
          : undefined,
    )
  }
}
